/**
 * RTWO Asset & Token Engine — Domain Models
 * Phase 14 | Module ID: RTWO-ASST-001
 * Version: 1.0.0-phase14
 *
 * Strongly typed domain models for the RTWO Asset & Token Engine.
 * Defines the general digital asset framework for the RTWO blockchain ecosystem.
 *
 * Truthful Data Policy:
 * - No fake assets, token supplies, holders, prices, transfers, or metadata.
 * - Tokens are NOT claimed as live or operational.
 * - Asset Engine is in Architecture Phase — Structure Complete.
 * - All runtime values are null until real Ledger Connection and Token Runtime are established.
 *
 * Source: Asset & Token Engine (RTWO-ASST-001)
 */

// ─────────────────────────────────────────────
// PRIMITIVE ID TYPES
// ─────────────────────────────────────────────

export type AssetId = string;
export type StandardId = string;
export type PolicyId = string;
export type PermissionId = string;
export type RuleId = string;
export type RecordId = string;
export type EventId = string;
export type MetadataId = string;
export type ASSTimestamp = number | null;

export const ASST_ENGINE_VERSION = {
  moduleId: 'RTWO-ASST-001',
  moduleName: 'Asset & Token Engine',
  version: '1.0.0-phase14',
  phase: 'Phase 14 — Asset & Token Engine',
  status: 'Structure Complete — Waiting for Real Asset Data',
  productionReadiness: 'Pending Ledger Connection and Token Runtime',
  policy:
    'Truthful Data Policy Enforced — No Fake Assets, Token Supplies, Holders, Prices, Transfers, or Metadata',
} as const;

// ─────────────────────────────────────────────
// ASSET ENGINE STATUS TYPES
// ─────────────────────────────────────────────

export type RTWOAssetEngineStatus =
  | 'asset_engine_ready'
  | 'no_assets_registered'
  | 'waiting_for_real_asset_data'
  | 'ledger_connection_pending'
  | 'token_runtime_not_operational'
  | 'structure_complete'
  | 'production_pending'
  | 'operational'
  | 'deprecated';

export const ASSET_ENGINE_STATUS_LABELS: Record<RTWOAssetEngineStatus, string> = {
  asset_engine_ready: 'Asset Engine Ready',
  no_assets_registered: 'No Assets Registered',
  waiting_for_real_asset_data: 'Waiting for Real Asset Data',
  ledger_connection_pending: 'Ledger Connection Pending',
  token_runtime_not_operational: 'Token Runtime Not Operational',
  structure_complete: 'Structure Complete',
  production_pending: 'Production Pending',
  operational: 'Operational',
  deprecated: 'Deprecated',
};

// ─────────────────────────────────────────────
// ASSET TYPE
// ─────────────────────────────────────────────

export type RTWOAssetType =
  | 'native_asset'
  | 'fungible_token'
  | 'non_fungible_token'
  | 'governance_token'
  | 'utility_token'
  | 'enterprise_token'
  | 'tokenized_asset';

export const ASSET_TYPE_LABELS: Record<RTWOAssetType, string> = {
  native_asset: 'Native Asset',
  fungible_token: 'Fungible Token',
  non_fungible_token: 'Non-Fungible Token',
  governance_token: 'Governance Token',
  utility_token: 'Utility Token',
  enterprise_token: 'Enterprise Token',
  tokenized_asset: 'Tokenized Asset',
};

export const ASSET_TYPE_DESCRIPTIONS: Record<RTWOAssetType, string> = {
  native_asset: 'The primary native asset of the RTWO blockchain — architecture defined, not live',
  fungible_token:
    'Interchangeable token units — architecture defined, token runtime not operational',
  non_fungible_token:
    'Unique non-interchangeable tokens — architecture defined, NFT runtime not operational',
  governance_token:
    'Voting and governance participation tokens — architecture defined, governance not operational',
  utility_token:
    'Platform utility and access tokens — architecture defined, utility runtime not operational',
  enterprise_token:
    'Enterprise-grade permissioned tokens — architecture defined, enterprise runtime not operational',
  tokenized_asset:
    'Real-world asset representations — architecture defined, tokenization not operational',
};

// ─────────────────────────────────────────────
// ASSET LIFECYCLE
// ─────────────────────────────────────────────

export type RTWOAssetLifecycleStage =
  'draft' | 'validated' | 'registered' | 'active' | 'suspended' | 'deprecated' | 'archived';

export const ASSET_LIFECYCLE_LABELS: Record<RTWOAssetLifecycleStage, string> = {
  draft: 'Draft',
  validated: 'Validated',
  registered: 'Registered',
  active: 'Active',
  suspended: 'Suspended',
  deprecated: 'Deprecated',
  archived: 'Archived',
};

export const ASSET_LIFECYCLE_DESCRIPTIONS: Record<RTWOAssetLifecycleStage, string> = {
  draft: 'Asset definition created — awaiting validation',
  validated: 'Asset definition validated — awaiting registration',
  registered: 'Asset registered in the RTWO Asset Registry',
  active: 'Asset is active and operational on the ledger',
  suspended: 'Asset operations temporarily suspended',
  deprecated: 'Asset marked for removal — no new operations',
  archived: 'Asset permanently archived — read-only history',
};

// ─────────────────────────────────────────────
// DOMAIN MODEL 1: RTWOAssetDefinition
// ─────────────────────────────────────────────

export type RTWOAssetDefinitionStatus =
  | 'architecture_ready'
  | 'no_assets_registered'
  | 'waiting_for_real_data'
  | 'draft'
  | 'validated'
  | 'registered'
  | 'active'
  | 'suspended'
  | 'deprecated'
  | 'archived';

export interface RTWOAssetDefinition {
  assetId: AssetId;
  assetSymbol: string;
  assetName: string;
  assetType: RTWOAssetType;
  decimals: number | null;
  issuerReference: string;
  status: RTWOAssetDefinitionStatus;
  createdAt: ASSTimestamp;
}

// ─────────────────────────────────────────────
// TOKEN STANDARD TYPES
// ─────────────────────────────────────────────

export type RTWOTokenStandardType =
  | 'rtwo_native'
  | 'rtwo_fungible'
  | 'rtwo_non_fungible'
  | 'rtwo_governance'
  | 'rtwo_enterprise'
  | 'rtwo_utility'
  | 'custom';

export type RTWOTokenStandardStatus =
  'architecture_defined' | 'not_implemented' | 'waiting_for_runtime' | 'implemented' | 'deprecated';

export const TOKEN_STANDARD_STATUS_LABELS: Record<RTWOTokenStandardStatus, string> = {
  architecture_defined: 'Architecture Defined',
  not_implemented: 'Not Implemented',
  waiting_for_runtime: 'Waiting for Runtime',
  implemented: 'Implemented',
  deprecated: 'Deprecated',
};

export type RTWOCompatibilityPolicy =
  'rtwo_native_only' | 'cross_standard_compatible' | 'enterprise_restricted' | 'not_configured';

// ─────────────────────────────────────────────
// DOMAIN MODEL 2: RTWOTokenStandard
// ─────────────────────────────────────────────

export interface RTWOTokenStandard {
  standardId: StandardId;
  standardName: string;
  standardType: RTWOTokenStandardType;
  version: string;
  compatibilityPolicy: RTWOCompatibilityPolicy;
  status: RTWOTokenStandardStatus;
}

// ─────────────────────────────────────────────
// SUPPLY POLICY TYPES
// ─────────────────────────────────────────────

export type RTWOSupplyType =
  'fixed' | 'capped' | 'uncapped' | 'deflationary' | 'inflationary' | 'not_configured';

export type RTWOMintingPolicy =
  | 'no_minting'
  | 'owner_only'
  | 'governance_controlled'
  | 'smart_contract_controlled'
  | 'not_configured';

export type RTWOBurningPolicy =
  | 'no_burning'
  | 'owner_only'
  | 'governance_controlled'
  | 'smart_contract_controlled'
  | 'not_configured';

export type RTWOSupplyPolicyStatus =
  'architecture_ready' | 'not_configured' | 'active' | 'suspended' | 'deprecated';

// ─────────────────────────────────────────────
// DOMAIN MODEL 3: RTWOAssetSupplyPolicy
// ─────────────────────────────────────────────

export interface RTWOAssetSupplyPolicy {
  policyId: PolicyId;
  assetId: AssetId;
  supplyType: RTWOSupplyType;
  maxSupplyReference: string;
  mintingPolicy: RTWOMintingPolicy;
  burningPolicy: RTWOBurningPolicy;
  status: RTWOSupplyPolicyStatus;
}

// ─────────────────────────────────────────────
// PERMISSION TYPES
// ─────────────────────────────────────────────

export type RTWOAssetPermissionType =
  'transfer' | 'mint' | 'burn' | 'freeze' | 'admin' | 'view' | 'not_configured';

export type RTWOAssetPermissionStatus =
  'architecture_ready' | 'not_configured' | 'active' | 'revoked' | 'expired';

// ─────────────────────────────────────────────
// DOMAIN MODEL 4: RTWOAssetPermission
// ─────────────────────────────────────────────

export interface RTWOAssetPermission {
  permissionId: PermissionId;
  assetId: AssetId;
  permissionType: RTWOAssetPermissionType;
  subjectReference: string;
  status: RTWOAssetPermissionStatus;
}

// ─────────────────────────────────────────────
// TRANSFER RULE TYPES
// ─────────────────────────────────────────────

export type RTWOTransferRuleType =
  | 'whitelist'
  | 'blacklist'
  | 'kyc_required'
  | 'time_lock'
  | 'amount_limit'
  | 'jurisdiction_restriction'
  | 'not_configured';

export type RTWOTransferRuleStatus =
  'architecture_ready' | 'not_configured' | 'active' | 'suspended' | 'deprecated';

// ─────────────────────────────────────────────
// DOMAIN MODEL 5: RTWOAssetTransferRule
// ─────────────────────────────────────────────

export interface RTWOAssetTransferRule {
  ruleId: RuleId;
  assetId: AssetId;
  ruleType: RTWOTransferRuleType;
  conditionReference: string;
  status: RTWOTransferRuleStatus;
}

// ─────────────────────────────────────────────
// REGISTRY RECORD TYPES
// ─────────────────────────────────────────────

export type RTWORegistryStatus =
  | 'architecture_ready'
  | 'no_assets_registered'
  | 'waiting_for_real_data'
  | 'pending_registration'
  | 'registered'
  | 'suspended'
  | 'deregistered';

// ─────────────────────────────────────────────
// DOMAIN MODEL 6: RTWOAssetRegistryRecord
// ─────────────────────────────────────────────

export interface RTWOAssetRegistryRecord {
  recordId: RecordId;
  assetId: AssetId;
  registryStatus: RTWORegistryStatus;
  ownerReference: string;
  createdAt: ASSTimestamp;
  updatedAt: ASSTimestamp;
}

// ─────────────────────────────────────────────
// ASSET EVENT TYPES
// ─────────────────────────────────────────────

export type RTWOAssetEventType =
  | 'asset_created'
  | 'asset_validated'
  | 'asset_registered'
  | 'asset_activated'
  | 'asset_suspended'
  | 'asset_deprecated'
  | 'asset_archived'
  | 'token_minted'
  | 'token_burned'
  | 'token_transferred'
  | 'permission_granted'
  | 'permission_revoked'
  | 'supply_policy_updated'
  | 'transfer_rule_updated'
  | 'metadata_updated';

export type RTWOAssetEventStatus =
  | 'architecture_ready'
  | 'no_events'
  | 'waiting_for_real_events'
  | 'emitted'
  | 'delivered'
  | 'failed';

// ─────────────────────────────────────────────
// DOMAIN MODEL 7: RTWOAssetEvent
// ─────────────────────────────────────────────

export interface RTWOAssetEvent {
  eventId: EventId;
  assetId: AssetId;
  eventType: RTWOAssetEventType;
  payloadReference: string;
  transactionReference: string;
  status: RTWOAssetEventStatus;
  emittedAt: ASSTimestamp;
}

// ─────────────────────────────────────────────
// METADATA TYPES
// ─────────────────────────────────────────────

export type RTWOMetadataStatus =
  | 'architecture_ready'
  | 'no_metadata'
  | 'waiting_for_real_data'
  | 'pending'
  | 'active'
  | 'deprecated';

// ─────────────────────────────────────────────
// DOMAIN MODEL 8: RTWOTokenMetadata
// ─────────────────────────────────────────────

export interface RTWOTokenMetadata {
  metadataId: MetadataId;
  assetId: AssetId;
  metadataReference: string;
  schemaVersion: string;
  status: RTWOMetadataStatus;
}

// ─────────────────────────────────────────────
// ASSET ENGINE EVENT TYPES
// ─────────────────────────────────────────────

export type ASSEventType =
  | 'asset_engine_initialized'
  | 'asset_definition_created'
  | 'token_standard_registered'
  | 'supply_policy_configured'
  | 'permission_configured'
  | 'transfer_rule_configured'
  | 'registry_record_created'
  | 'asset_event_emitted'
  | 'metadata_configured'
  | 'readiness_checked';

export type ASSEventHandler = (eventType: ASSEventType, payload: unknown) => void;

// ─────────────────────────────────────────────
// PREDEFINED TOKEN STANDARDS (Architecture)
// ─────────────────────────────────────────────

export const RTWO_TOKEN_STANDARDS: RTWOTokenStandard[] = [
  {
    standardId: 'std-rtwo-native-001',
    standardName: 'RTWO Native Asset Standard',
    standardType: 'rtwo_native',
    version: '1.0.0-architecture',
    compatibilityPolicy: 'rtwo_native_only',
    status: 'architecture_defined',
  },
  {
    standardId: 'std-rtwo-fungible-001',
    standardName: 'RTWO Fungible Token Standard',
    standardType: 'rtwo_fungible',
    version: '1.0.0-architecture',
    compatibilityPolicy: 'rtwo_native_only',
    status: 'architecture_defined',
  },
  {
    standardId: 'std-rtwo-nft-001',
    standardName: 'RTWO Non-Fungible Token Standard',
    standardType: 'rtwo_non_fungible',
    version: '1.0.0-architecture',
    compatibilityPolicy: 'rtwo_native_only',
    status: 'architecture_defined',
  },
  {
    standardId: 'std-rtwo-governance-001',
    standardName: 'RTWO Governance Token Standard',
    standardType: 'rtwo_governance',
    version: '1.0.0-architecture',
    compatibilityPolicy: 'rtwo_native_only',
    status: 'architecture_defined',
  },
  {
    standardId: 'std-rtwo-enterprise-001',
    standardName: 'RTWO Enterprise Token Standard',
    standardType: 'rtwo_enterprise',
    version: '1.0.0-architecture',
    compatibilityPolicy: 'enterprise_restricted',
    status: 'architecture_defined',
  },
  {
    standardId: 'std-rtwo-utility-001',
    standardName: 'RTWO Utility Token Standard',
    standardType: 'rtwo_utility',
    version: '1.0.0-architecture',
    compatibilityPolicy: 'rtwo_native_only',
    status: 'architecture_defined',
  },
  {
    standardId: 'std-rtwo-tokenized-001',
    standardName: 'RTWO Tokenized Asset Standard',
    standardType: 'custom',
    version: '1.0.0-architecture',
    compatibilityPolicy: 'enterprise_restricted',
    status: 'architecture_defined',
  },
];
